import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Formik } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { Col, DatePicker, Input, Row, Spin, Table, notification } from "antd";
import NumberFormat from "react-number-format";

import ButtonStandard from "../../utils/button";
import { Body, Container, Footer, ResultPanel, SectionTitle } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import { executeProductionBatch } from "../../../services/productionOrderService";
import { resolveProductionOrderError } from "../../../utils/productionOrderErrors";

const DrawerProductionBatch = ({
  visible,
  order,
  batchNumber,
  onClose,
  onSave,
}) => {
  const formikRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    manufactureDate: null,
    observation: null,
    quantities: {},
  });

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const validationSchema = Yup.object().shape({
    manufactureDate: Yup.mixed()
      .required()
      .test(
        "valid-date",
        "invalid date",
        (value) => value != null && moment(value).isValid(),
      ),
  });

  const numberOfBatches =
    order?.numberOfBatches != null && order.numberOfBatches > 0
      ? order.numberOfBatches
      : 1;

  const plannedOf = useCallback(
    (item) =>
      item.plannedOriginalMatterKg != null
        ? item.plannedOriginalMatterKg / numberOfBatches
        : 0,
    [numberOfBatches],
  );

  useEffect(() => {
    if (!visible || order == null) {
      return;
    }

    const quantities = (order.items ?? []).reduce(
      (accumulator, item) => ({
        ...accumulator,
        [item.supplementId]: Number(plannedOf(item).toFixed(2)),
      }),
      {},
    );

    const values = {
      manufactureDate: order.productionDate
        ? moment(order.productionDate)
        : moment(),
      observation: null,
      quantities,
    };

    setForm(values);

    if (formikRef.current) {
      formikRef.current.setValues(values);
    }
  }, [visible, order, plannedOf]);

  const handleSubmitForm = useCallback(
    async (values) => {
      setIsLoading(true);
      try {
        const body = {
          batchNumber,
          manufactureDate: moment(
            values.manufactureDate ?? order.productionDate ?? undefined,
          ).format("YYYY-MM-DD"),
          observation: values.observation,
          ingredients: (order.items ?? []).map((item) => ({
            supplementId: item.supplementId,
            quantityRealized: values.quantities[item.supplementId] ?? 0,
          })),
        };

        await executeProductionBatch({ groupId, farmId, id: order.id, body });

        notification.success({
          message: translation.production.messages.successExecute,
        });
        onSave();
      } catch (error) {
        notification.error({
          message: resolveProductionOrderError(
            error,
            translation,
            translation.production.messages.errorExecute,
          ),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [batchNumber, farmId, groupId, onSave, order, translation],
  );

  const deviationOf = (item, values) => {
    const planned = plannedOf(item);
    const used = values.quantities[item.supplementId];

    if (planned <= 0 || used == null) {
      return 0;
    }

    return ((used - planned) / planned) * 100;
  };

  const totalOf = (values) =>
    (order?.items ?? []).reduce(
      (total, item) => total + (values.quantities[item.supplementId] ?? 0),
      0,
    );

  return (
    <Container
      title={translation.production.batch.title}
      width={880}
      onClose={onClose}
      maskClosable={false}
      visible={visible}
    >
      <Formik
        ref={formikRef}
        enableReinitialize={true}
        initialValues={form}
        onSubmit={handleSubmitForm}
        validationSchema={validationSchema}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <Spin spinning={isLoading}>
            <form onSubmit={handleSubmit} autoComplete="off">
              <Body>
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}

                <ResultPanel>
                  <Row type="flex" gutter={8}>
                    <Col xs={12} sm={12} md={5} lg={5} xl={5}>
                      <div className="resultItem">
                        <span className="label">
                          {translation.production.batch.order}
                        </span>
                        <span className="value">{order?.code}</span>
                      </div>
                    </Col>
                    <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                      <div className="resultItem">
                        <span className="label">
                          {translation.production.batch.batchLabel}
                        </span>
                        <span className="value">#{batchNumber ?? "-"}</span>
                      </div>
                    </Col>
                    <Col xs={12} sm={12} md={8} lg={8} xl={8}>
                      <div className="resultItem">
                        <span className="label">
                          {translation.production.batch.recipe}
                        </span>
                        <span className="value">{order?.supplementName}</span>
                      </div>
                    </Col>
                    <Col xs={12} sm={12} md={7} lg={7} xl={7}>
                      <div className="resultItem">
                        <span className="label">
                          {translation.production.batch.plannedPerBatch}
                        </span>
                        <span className="value">
                          {numberMask(order?.dryMatterPerBatchKg)} kg MS
                        </span>
                      </div>
                    </Col>
                  </Row>
                </ResultPanel>

                <Row type="flex" gutter={8} style={{ marginTop: 12 }}>
                  <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                    <Row>
                      <label
                        className={
                          errors.manufactureDate && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.production.batch.manufactureDate}*
                      </label>
                    </Row>
                    <Row>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        value={
                          values.manufactureDate
                            ? moment(values.manufactureDate)
                            : undefined
                        }
                        placeholder={translation.defaultDatePickerPlaceholder}
                        onChange={(date) =>
                          setFieldValue("manufactureDate", date)
                        }
                      />
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                    <Row>
                      <label>{translation.production.batch.observation}</label>
                    </Row>
                    <Row>
                      <Input
                        value={values.observation ?? ""}
                        onChange={(event) =>
                          setFieldValue("observation", event.target.value)
                        }
                      />
                    </Row>
                  </Col>
                </Row>

                <SectionTitle>
                  {translation.production.batch.ingredients}
                </SectionTitle>
                <Table
                  rowKey="supplementId"
                  size="small"
                  pagination={false}
                  dataSource={order?.items ?? []}
                  style={{ width: "100%", marginTop: 8 }}
                >
                  <Table.Column
                    title={translation.production.batch.columns.ingredient}
                    dataIndex="supplementName"
                    align="left"
                  />
                  <Table.Column
                    title={translation.production.batch.columns.batch}
                    align="left"
                    render={(text, record) =>
                      record.batchNumber ??
                      translation.productionOrder.form.noBatch
                    }
                  />
                  <Table.Column
                    title={translation.production.batch.columns.planned}
                    align="right"
                    render={(text, record) => numberMask(plannedOf(record))}
                  />
                  <Table.Column
                    title={translation.production.batch.columns.realized}
                    align="right"
                    render={(text, record) => (
                      <NumberFormat
                        customInput={Input}
                        value={values.quantities[record.supplementId] ?? ""}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        allowNegative={false}
                        onValueChange={({ floatValue }) =>
                          setFieldValue("quantities", {
                            ...values.quantities,
                            [record.supplementId]:
                              floatValue == null ? 0 : floatValue,
                          })
                        }
                      />
                    )}
                  />
                  <Table.Column
                    title={translation.production.batch.columns.deviation}
                    align="right"
                    render={(text, record) => {
                      const deviation = deviationOf(record, values);
                      return (
                        <span
                          style={{
                            color:
                              Math.abs(deviation) < 0.01
                                ? undefined
                                : deviation > 0
                                  ? "#D44C4C"
                                  : "#4A85AE",
                          }}
                        >
                          {numberMask(deviation)} %
                        </span>
                      );
                    }}
                  />
                </Table>

                <Row type="flex" justify="end" style={{ marginTop: 12 }}>
                  <Col>
                    <span>
                      {translation.production.batch.totalUsed}{" "}
                      <strong>{numberMask(totalOf(values))} kg</strong>
                    </span>
                  </Col>
                </Row>
              </Body>
              <Footer>
                <Row type="flex">
                  <Col span={24}>
                    <div className="buttonsDiv">
                      <ButtonStandard
                        type="button"
                        buttonType="type7"
                        onClick={onClose}
                      >
                        {translation.buttons.cancel}
                      </ButtonStandard>
                      <ButtonStandard type="submit" buttonType="type6">
                        {translation.production.batch.execute}
                      </ButtonStandard>
                    </div>
                  </Col>
                </Row>
              </Footer>
            </form>
          </Spin>
        )}
      />
    </Container>
  );
};

export default DrawerProductionBatch;
